<template>
  <div class="base-widget" ref="exportBox" :class="{ 'is-exporting': isExporting }">
    <div class="base-widget__header">
      <h3 class="base-widget__title">{{ title }}</h3>
      <v-menu content-class="download-menu ml-2" v-if="!isExporting">
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            v-if="!edit"
            v-bind="attrs"
            v-on="on"
            color="accent-purple ml-2"
            class="base-widget__export-button action-icon"
            icon
          >
            <v-icon color="accent-purple">mdi-tray-arrow-down</v-icon>
          </v-btn>
        </template>
        <v-list dense>
          <v-list-item>
            <v-list-item-title style="cursor: pointer" @click="getWidgetReport">
              {{ Lang.exportCsvAction }}
            </v-list-item-title>
          </v-list-item>
          <v-list-item>
            <v-list-item-title style="cursor: pointer" @click="onExportPng">
              {{ Lang.exportPNGAction }}
            </v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>

      <div class="base-widget__controls-container">
        <slot name="widget-control" />
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              v-if="edit"
              class="base-widget__more ml-5 action-icon"
              @click="onEditBtn"
              icon
              v-bind="attrs"
              v-on="on"
            >
              <v-icon>mdi-dots-horizontal</v-icon>
            </v-btn>
          </template>
          <span>{{ Lang.configureWidget }}</span>
        </v-tooltip>
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              v-if="edit"
              class="base-widget__more action-icon"
              @click="onWidgetDelete"
              icon
              v-bind="attrs"
              v-on="on"
            >
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </template>
          <span>{{ Lang.removeWidget }}</span>
        </v-tooltip>
      </div>
    </div>
    <slot />
    <SpinnerLoader v-if="!isLoadingUserSettings && !isLoading && isWidgetLoading" />
    <EditDrawer v-model="isEditMode" :freeMode="freeMode">
      <slot name="edit" :hideDrover="hideDrover" />
    </EditDrawer>
  </div>
</template>

<script lang="ts">
import Vue from "vue";
import { mapGetters, mapMutations } from "vuex";

import EditDrawer from "@/components/drawers/EditDrawer.vue";
import SpinnerLoader from "@/components/loaders/SpinnerLoader.vue";
import { SAFARI_BROWSER, detectBrowser } from "@/helpers/export";
import Lang from "@/lang";
import ROUTES from "@/settings/routes";
import store from "@/store";
import { ContentWidget } from "@/types/layouts";
import { exportNodeToPng } from "@/utils/exportNodeToPng";

export default Vue.extend({
  name: "BaseWidget",
  components: {
    EditDrawer,
    SpinnerLoader,
  },
  props: {
    title: String,
    freeMode: Boolean,
    widgetId: String,
    edit: Boolean,
    isWidgetLoading: Boolean,
    content: Object as () => ContentWidget,
    getWidgetReport: Function,
  },

  data() {
    return {
      isEditMode: false,
      Lang,
      isExporting: false,
    };
  },

  computed: {
    ...mapGetters("userSettings", ["dashboards", "filterParams", "isLoadingUserSettings"]),
    ...mapGetters("catalogs", ["isLoading"]),

    dashboardName(): string {
      return this.$route.params.dashboardName;
    },
  },

  methods: {
    ...mapMutations("pageEdit", ["updateSelectWidget", "updateShowDrawerConfig"]),
    ...mapMutations("userSettings", ["deleteWidget"]),

    async onExportPng() {
      const browser = detectBrowser();
      if (browser === SAFARI_BROWSER) {
        store.commit("app/showAlert", {
          message: Lang.pngExportUnavailableInSafari,
          type: "info",
        });
        return;
      }

      this.isExporting = true;
      const box = this.$refs.exportBox as HTMLElement | undefined;
      if (!box) return;

      await this.$nextTick();

      await exportNodeToPng(box, this.content?.title ?? "export", {
        filter: (n: HTMLElement) => !n.closest?.(".no-export"),
      });
      await this.$nextTick();
      this.isExporting = false;
    },

    onEditBtn(): void {
      this.updateSelectWidget(this.content);
      const editRoute = `${ROUTES.EDIT_PAGE.PATH}/${this.dashboardName}`;
      if (this.$route.path !== editRoute) {
        this.$router.push(`${ROUTES.EDIT_PAGE.PATH}/${this.dashboardName}`);
      }

      this.updateShowDrawerConfig(true);
    },

    hideDrover(): void {
      this.isEditMode = false;
    },

    onWidgetDelete(): void {
      this.deleteWidget({ dashboardId: this.dashboardName, widgetId: this.widgetId });
    },
  },
});
</script>

<style lang="scss" scoped>
@import "@/scss/theme.scss";
@import "@/scss/custom_variables.scss";

.base-widget {
  height: 100%;
  display: flex;
  flex-direction: column;
  padding: $space-medium;
  border-radius: $border-radius;
  position: relative;
  overflow: hidden;
  border: 2px solid transparent;

  &:hover {
    border-color: var(--v-accent-global-base);
  }

  &.is-exporting {
    border-color: transparent !important;

    &:hover {
      border-color: transparent !important;
    }
  }

  &__header {
    display: flex;
    min-height: 36px;
    margin-bottom: $space-small;
    align-items: center;

    &::before {
      content: "";
      display: inline-block;
      height: 32px;
      width: 8px;
      margin-right: 16px;
      vertical-align: bottom;
      background-color: var(--v-secondary-base);
      border-radius: 4px;
    }
  }

  &__title {
    font-size: 20px;
    font-weight: 600;
    line-height: 1.2em;
  }

  &__controls-container {
    display: flex;
    margin-left: auto;
    align-items: center;
    flex-shrink: 0;
  }
}
</style>

<style lang="scss">
@import "@/scss/custom_variables.scss";

.base-widget {
  &__export-button {
    margin-left: 8px;

    .v-icon:hover,
    .v-icon:active {
      color: var(--v-accent-global-base) !important;
    }
  }
}

.vue-grid-item > .vue-resizable-handle {
  width: 20px !important;
  height: 20px !important;
  bottom: 8px !important;
  right: 8px !important;
  padding: 0 !important;
  background-image: url("~@/assets/icons/resizable-handle.svg") !important;
  background-size: 16px !important;
}

.vue-grid-item.vue-grid-placeholder {
  background-color: $color-6 !important;
}

.v-application.theme--light,
.pdf-container.theme--light {
  .base-widget {
    background-color: #ffffff;
    box-shadow: $box-shadow-light;
  }
}

.v-application.theme--dark,
.pdf-container.theme--dark {
  .base-widget {
    background-color: rgba(53, 56, 94, 0.55);
  }
}

.base-widget.is-exporting,
.base-widget.is-exporting * {
  pointer-events: none !important;
}
</style>
